'use client';

import { createContext, useCallback, useState, useContext } from 'react';
import { Dialog, Transition, DialogPanel } from '@headlessui/react';
import SignupForm from './signup-form';
import LoginForm from './login-form';
import CheckoutModal from './checkout/checkout-modal';
import ModernAuthModal from './auth/modern-auth-modal';
import { useAuthSystem } from '@/app/treatments-provider';
import type { AuthSystem } from '@/app/utils/auth-system';

export const MODAL_TYPES = {
  SIGNUP: 'SIGNUP',
  LOGIN: 'LOGIN',
  CHECKOUT: 'CHECKOUT',
  MODERN_AUTH_LOGIN: 'MODERN_AUTH_LOGIN',
  MODERN_AUTH_SIGNUP: 'MODERN_AUTH_SIGNUP',
} as const;

type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES];

const MODAL_COMPONENTS: Record<ModalType, React.ComponentType<Record<string, unknown>>> = {
  [MODAL_TYPES.SIGNUP]: SignupForm,
  [MODAL_TYPES.LOGIN]: LoginForm,
  [MODAL_TYPES.CHECKOUT]: CheckoutModal,
  [MODAL_TYPES.MODERN_AUTH_LOGIN]: ModernAuthModal,
  [MODAL_TYPES.MODERN_AUTH_SIGNUP]: ModernAuthModal,
};

/**
 * All auth-launching calls are normalized here. Callers request the logical
 * login or signup modal; the global treatment selects its implementation.
 */
function authModalTypeForSystem(modalType: ModalType, authSystem: AuthSystem): ModalType {
  if (modalType === MODAL_TYPES.LOGIN || modalType === MODAL_TYPES.MODERN_AUTH_LOGIN) {
    return authSystem === 'modern' ? MODAL_TYPES.MODERN_AUTH_LOGIN : MODAL_TYPES.LOGIN;
  }

  if (modalType === MODAL_TYPES.SIGNUP || modalType === MODAL_TYPES.MODERN_AUTH_SIGNUP) {
    return authSystem === 'modern' ? MODAL_TYPES.MODERN_AUTH_SIGNUP : MODAL_TYPES.SIGNUP;
  }

  return modalType;
}

export interface ModalStateType {
  modalType: ModalType | null;
  modalProps?: Record<string, unknown>;
}

export interface ModalContextType {
  showModal: (modalType: ModalType, modalProps?: Record<string, unknown>) => void;
  hideModal: () => void;
  state: ModalStateType | null;
}

const initialValue: ModalContextType = {
  showModal: () => {},
  hideModal: () => {},
  state: null,
};

export const ModalContext = createContext<ModalContextType>(initialValue);

export const useModalContext = () => useContext(ModalContext);

export default function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modalState, setModalState] = useState<ModalStateType | null>(null);
  const authSystem = useAuthSystem();

  const { modalType, modalProps } = modalState || {};

  const setClosed = () => {
    setModalState({
      ...modalState,
      modalType: null,
      modalProps: {},
    });
  };

  const showModal = useCallback(
    (modalType: ModalType, modalProps: Record<string, unknown> = {}) => {
      setModalState((current) => ({
        ...current,
        modalType: authModalTypeForSystem(modalType, authSystem),
        modalProps,
      }));
    },
    [authSystem],
  );

  const hideModal = () => {
    // setModalState({
    //   ...modalState,
    //   modalType: null,
    //   modalProps: {},
    // });
    setClosed();
  };

  const renderComponent = () => {
    if (modalType == null) {
      return <></>;
    }
    const ModalComponent = MODAL_COMPONENTS[modalType];
    if (ModalComponent == null) {
      return <></>;
    }

    // Let the auth forms switch to the opposing form, carrying the same props
    // (notably onSuccess) forward so the post-auth behavior is preserved.
    const authSwitchProps: Record<string, unknown> = {};
    if (modalType === MODAL_TYPES.LOGIN) {
      authSwitchProps.onSwitchToSignup = () => showModal(MODAL_TYPES.SIGNUP, { ...modalProps });
    } else if (modalType === MODAL_TYPES.SIGNUP) {
      authSwitchProps.onSwitchToLogin = () => showModal(MODAL_TYPES.LOGIN, { ...modalProps });
    } else if (modalType === MODAL_TYPES.MODERN_AUTH_LOGIN) {
      authSwitchProps.initialMode = 'login';
    } else if (modalType === MODAL_TYPES.MODERN_AUTH_SIGNUP) {
      authSwitchProps.initialMode = 'signup';
    }

    // return <ModalComponent id="global-modal" {...modalProps} />;
    return (
      <>
        <Transition
          show={true}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-out duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-50 bg-black opacity-75 transition-opacity"
            aria-hidden="true"
          />
        </Transition>

        <Dialog onClose={setClosed} open className="relative z-50">
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel
              className={`relative bg-white ${
                modalType === MODAL_TYPES.CHECKOUT
                  ? 'w-auto max-w-dvw max-h-[90vh] overflow-y-auto p-0 min-w-sm md:min-w-md lg:min-w-lg'
                  : 'max-w-sm md:max-w-lg p-8 pt-12'
              }`}
            >
              {/* Close button - hidden for checkout since it has its own */}
              {modalType !== MODAL_TYPES.CHECKOUT && (
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button onClick={hideModal}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      overflow="visible"
                      stroke="black"
                      strokeWidth="4"
                      strokeLinecap="round"
                      className="stroke-zinc-800 hover:stroke-zinc-600 cursor-pointer"
                    >
                      <line x2="12" y2="12" />
                      <line x1="12" y2="12" />
                    </svg>
                  </button>
                </div>
              )}
              <ModalComponent {...modalProps} {...authSwitchProps} onClose={hideModal} />
            </DialogPanel>
          </div>
        </Dialog>
      </>
    );
  };

  return (
    <ModalContext.Provider value={{ state: modalState, showModal, hideModal }}>
      {renderComponent()}
      {children}
    </ModalContext.Provider>
  );
}
